import React, { useEffect, useState } from 'react'
import { api, formatDateTime } from '../../utils/api'
import { methodLabel } from './collectionMethods'

const FIELD_LABELS = {
  send_type: 'Method',
  carrier: 'Carrier',
  tracking_number: 'Tracking',
  label_link: 'Label',
  collection_date: 'Collection date',
  dropoff_slot_start: 'Drop-off slot',
}

const fieldLabel = name => FIELD_LABELS[name] ?? name

function show(field, value) {
  if (value == null || value === '') return 'none'
  return field === 'send_type' ? methodLabel(value) : String(value)
}

// One line per field. Events from before the change record carry only the
// names of the fields written, so they list names with no from and to.
function describe(payload) {
  const changes = payload?.changes
  if (changes && Object.keys(changes).length) {
    return Object.entries(changes).map(([field, { from, to }]) => ({
      field: fieldLabel(field),
      from: show(field, from),
      to: show(field, to),
    }))
  }
  return (payload?.fields ?? []).map(field => ({ field: fieldLabel(field) }))
}

/**
 * The booking's change record for the Collections drawer (Trac #1404): what
 * changed, from and to, who and when, newest first. Supplementary, so a failure
 * shows one quiet line and never breaks the drawer.
 */
export default function CollectionHistory({ dealId, quoteId }) {
  const [state, setState] = useState({ status: 'loading', events: [] })

  useEffect(() => {
    let live = true
    const hint = quoteId ? `?quote_id=${encodeURIComponent(quoteId)}` : ''
    api.get(`/api/deals/${dealId}/events${hint}`)
      .then(r => {
        if (!live) return
        const events = (r?.events ?? [])
          .filter(e => typeof e.kind === 'string' && e.kind.toLowerCase().startsWith('collection_'))
          .sort((a, b) => new Date(b.occurred_at) - new Date(a.occurred_at))
        setState({ status: 'ready', events })
      })
      .catch(() => { if (live) setState({ status: 'error', events: [] }) })
    return () => { live = false }
  }, [dealId, quoteId])

  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-muted)' }}>CHANGE HISTORY</div>
      <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius)', padding: '8px 12px', fontSize: 12 }}>
        {state.status === 'loading' && <div style={{ color: 'var(--color-text-muted)' }}>Loading...</div>}
        {state.status === 'error' && <div style={{ color: 'var(--color-text-muted)' }}>Could not load the change history.</div>}
        {state.status === 'ready' && state.events.length === 0 && (
          <div style={{ color: 'var(--color-text-muted)' }}>No changes recorded yet.</div>
        )}
        {state.status === 'ready' && state.events.length > 0 && (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {state.events.map((e, i) => (
              <li key={`${e.occurred_at}-${i}`} style={{ padding: '6px 0', borderTop: i ? '1px solid var(--color-border)' : 'none' }}>
                {describe(e.payload).map((c, j) => (
                  <div key={j}>
                    <strong>{e.payload?.box ? `Box ${e.payload.box} ` : ''}{c.field}</strong>
                    {c.from !== undefined && <span>: {c.from} → {c.to}</span>}
                  </div>
                ))}
                <div style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>
                  {e.payload?.actor ? `${e.payload.actor}, ` : ''}{formatDateTime(e.occurred_at)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
